import { BreadCrumbOption } from '@service-victoria/component-library/components/BreadCrumb';
import { nanoid } from 'nanoid';

export enum SelectActivityProviderStatus {
  Success = 'success',
  Failed = 'failed'
}

const listToOptions = (list: string[]) =>
  [''].concat(list).map(item => ({
    label: item,
    value: item
  }));

export const RedemptionPage = {
  title: 'Check and redeem your Get Active Kids vouchers',
  transactionName: 'get-active-kids-redemption'
};

export const ProviderDetailsPage = {
  title: 'Update your Get Active Kids details',
  transactionName: 'get-active-kids-update-details'
};

export const BREADCRUMBS: BreadCrumbOption[] = [
  {
    text: 'Service',
    link: 'https://service.vic.gov.au/services/service'
  },
  {
    text: 'New Transaction',
    link: 'https://service.vic.gov.au/services/service/new-transaction'
  }
];

export const STORAGE_STATE_KEY = 'ServiceVictoria.NewTransaction.State';

export const ProviderStorageKeys = {
  State: 'ServiceVictoria.GetActiveKids.ManageGetActiveKidsProvider.State',
  Wizard: 'ServiceVictoria.GetActiveKids.ManageGetActiveKidsProvider.Wizard'
};

export const RedemptionStorageKeys = {
  State: 'ServiceVictoria.GetActiveKids.ManageGetActiveKidsRedemption.State',
  Wizard: 'ServiceVictoria.GetActiveKids.ManageGetActiveKidsRedemption.Wizard'
};

export const GenericValidationErrors = {
  required: 'We can’t help you without this information.',
  maxLength: (limit: number): string => `The max length for this field is ${limit} characters.`
};

export const Routes = {
  Root: '/',
  Home: '/home',
  AccountCallback: '/callback/login',
  Dashboard: '/dashboard',
  Redemption: '/redemption',
  ProviderDetailsTransaction: '/update-provider',
  TechnicalError: '/technical-error',
  ChangeAuthorisedContact: '/change-authorised-contact',
  Summary: '/summary',
  Others: '*'
};

export const headerProps = {
  fetchItemsFromAPI: true,
  showSideNav: true
};

export const breadCrumbs: BreadCrumbOption[] = [
  {
    text: 'Outdoor and recreation',
    link: 'https://www.service.vic.gov.au/find-services/outdoor-and-recreation'
  },
  {
    text: 'Get Active Kids provider dashboard',
    link: ''
  }
];

export const PROVIDER_REGISTRATION_PATH = '/services/register-as-get-active-kids-provider';

export const PROGRAM_GUIDELINES_URL =
  'https://www.getactive.vic.gov.au/wp-content/uploads/2024/08/GAKVP-R8-Registered-Activity-Provider-Guidelines.pdf';

export const OUTDOOR_GAK_PATH = '/find-services/outdoor-and-recreation/get-active-kids';

/**
 * Maximum time allowed for keeping the user ssession active wihtout any action
 */
export const SESSION_TIMEOUT_THRESHOLD_MILLISECONDS = 300000;

/**
 * nonce unqiue code for CSP protection
 */
export const nonce = 'nonce-' + nanoid();

export const STATUS_CODES = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500
};

export const REGEX = {
  POSTCODE_VIC: /^(?:(?:[3]\d|[0-7]|0?[28]|0?9(?=09))(?:\d{2}))$/,
  ABN: /^(\d *?){11}$/
};

/**
 * Date constants values
 */
export const Dates = {
  /**
   * Char seperator between the date different sections (day, month, year)
   */
  DATE_DIVIDER_CHAR: '/',

  /**
   * Date format for full mode
   */
  FULL_DATE_FORMAT: 'DD/MM/YYYY',

  /**
   * Date format for partial mode
   */
  MONTH_YEAR_FORMAT: 'MM/YYYY',

  /**
   * Display date format in UX
   */
  DISPLAY_FORMAT: 'DD MMMM YYYY'
};

export const DefaultErrorResponse = {
  title: 'Sorry, there was a problem processing this request.',
  value: 'Please try again.'
};

/**
 * user setttings as part of OIDC
 */
export const UserManagerSettings = {
  scope: 'openid customer:rw profile email',
  authority: window.CONFIG.idamURL,
  client_id: window.CONFIG.idamClientId
};

export const USER_MANAGER_SESSION_KEY = `sv-tokenuser:${UserManagerSettings.authority}:${UserManagerSettings.client_id}`;

export const REDEMPTION_BROADCAST_CHANNEL_KEY = 'sv-getactivekids-voucher-redemption';

export const PROVIDER_BROADCAST_CHANNEL_KEY = 'sv-getactivekids-manage-provider';

const activityTypes = [
  'Adaptative Snowsports',
  'Archery',
  'Artistic Swimming',
  'Athletics',
  'AFL - Australian Rules Football',
  'Badminton',
  'Para badminton and Air badminton',
  'Balloon Football/ Netball',
  'Ballroom Dancing',
  'Baseball',
  'Basketball',
  'Biathlon',
  'Bicycle Motocross, Road and Track Cycling, Mountain Biking',
  'Billiards and Snooker',
  'Bocce',
  'Boxing',
  'Bushwalking',
  'Calisthenics',
  'Climbing',
  'Cricket',
  'Croquet',
  'Curling',
  'Dance (all styles)',
  'Darts',
  'Diving',
  'Junior Drag Racing',
  'Dragon Boat',
  'Eightball/Pool',
  'Equestrian',
  'Fencing',
  'Flying Disc',
  'Futsal',
  'Soccer/Football',
  'Girl Guides',
  'Gliding',
  'Goalball',
  'Golf',
  'Gridiron',
  'Gym and fitness programs',
  'Gymnastics',
  'Handball',
  'Hang Gliding and Paragliding',
  'Hockey',
  'Ice sports including short track speed skating',
  'Ice Hockey',
  'Indoor Sports',
  'Judo',
  'Karate',
  'Kart Racing',
  'Kendo',
  'Kung Fu',
  'Lacrosse',
  'Lawn Bowls',
  'Life Saving',
  'Modern Pentathlon',
  'Motorcycling Riding',
  'Motorsport',
  'Multi-sport and recreation programs',
  'Netball',
  'Orienteering',
  'Outdoor Recreation/Outdoor Education',
  'Outrigger Canoe Racing',
  'Paddle activities including canoeing, kayaking',
  'Parachuting',
  'Power Boat Racing',
  'Recreational Fishing',
  'Roller sports',
  'Rowing',
  'Rugby League',
  'Rugby Union',
  'Scouts',
  'Sailing',
  'Shooting',
  'Skateboarding',
  'Snowsports',
  'Softball',
  'Speed skating',
  'Squash and Racquetball',
  'Surfing',
  'Swimming',
  'Table Tennis',
  'Taekwondo',
  'Tennis',
  'Tenpin Bowling',
  'Touch Football',
  'Triathlon',
  'Underwater Hockey',
  'Volleyball',
  'Water Polo',
  'Waterskiing and Wakeboarding',
  'Weightlifting',
  'Wheelchair Rugby',
  'Wrestling'
];

export const activityTypeOptions = listToOptions(activityTypes);

export const MAX_RETRIES = 3;

export const ALLOWED_STATES = ['VIC', 'NSW', 'SA'];

export const AddressValidationErrors = {
  noAddressChosen:
    "Please choose an address from the suggestions that appear below the address field. If you can't see your address listed, choose the last option and enter your address manually.",
  required: 'Please enter a valid street address.',
  noPOBox:
    'Please enter a valid street address that the activity is located at. PO Boxes are not permitted.',
  postcode: 'Please enter a 4-digit postcode.',
  state: 'Please select a State/Territory.',
  suburb: 'Please enter a valid suburb.'
};
